import styled from '@emotion/styled'
import { AdmRuleHistory, FloorRule } from '@pubstack/common/src/adm/rules'
import { CurrencySymbol } from '@pubstack/common/src/currency'
import { countStacksUse } from '@pubstack/common/src/stack'
import { FunctionComponent, useState } from 'react'
import { Colors } from '~/assets/style/colors'
import Button from '~/components/Button'
import { HistoryFlyout } from '~/components/HistoryFlyout'
import { useGlobalFlyout } from '~/components/layout/GlobalFlyout'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { Link } from '~/components/Link'
import { PageContent } from '~/components/PageContent'
import { Pagination } from '~/components/Pagination'
import { Tooltip } from '~/components/Tooltip'
import Table, { EmptyTable, handleTableSort, onColumnSort, SortableColumn, TableColumns } from '~/components/table/Table'
import TableCell from '~/components/table/TableCell'
import TableRow from '~/components/table/TableRow'
import { ArchiveRuleModal } from '~/feature-components/ArchiveRuleModal'
import { RuleListToolbar } from '~/feature-components/RuleListToolbar'
import { useClientPagination } from '~/hooks/useClientPagination'
import { PureUsedStackTooltip } from '~/modules/adm/rules/PureUsedStackTooltip'
import { WithClassName } from '~/types/utils'
import { SUPPORT_MAIL } from '~/utils/constants'
import { displayWithCurrency } from '~/utils/string'
import { FloorRuleFlyoutPage } from './FloorRuleFlyoutPage'

/** Shared with the refresh and lazy loading lists, whose detail flyouts are the same component. */
const DETAILS_FLYOUT_WIDTH = '600px'

type PureFloorPricePageProps = WithClassName & {
  floorPriceRules: FloorRule[]
  currencySymbol: CurrencySymbol
  isLoading: boolean
  isError: boolean
  breadcrumbs: React.ReactNode
  onCreateRule: () => void
  onEditRule: (id: string) => void
  floorPriceRulesHistory?: AdmRuleHistory
  onArchiveRule: (id: string) => void | Promise<unknown>
  isArchiving: boolean
  onNavigateToArchived: () => void
  canCreate: boolean
  canEdit: boolean
  canArchive: boolean
  /** What a non-owner is told on the controls they cannot press. */
  permissionDisabledReason?: string
}

const ActionsTableCell = styled(TableCell)`
  width: 0px; /** necessary to get the last cell to shrink down to hug action buttons */
`

const ButtonActions = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
`

type FloorRuleListed = Omit<FloorRule, 'creationTime' | 'technicalId'>

const UnstyledPureFloorPricePage: FunctionComponent<PureFloorPricePageProps> = ({
  className,
  floorPriceRules,
  currencySymbol,
  isLoading,
  isError,
  breadcrumbs,
  onCreateRule,
  onEditRule,
  floorPriceRulesHistory,
  onArchiveRule,
  isArchiving,
  onNavigateToArchived,
  canCreate,
  canEdit,
  canArchive,
  permissionDisabledReason,
}) => {
  const [columns, setColumns] = useState<TableColumns<FloorRuleListed>>([
    {
      name: 'Rule',
      isSortable: true,
      order: 'none',
      attributeSort: 'name',
    },
    {
      name: 'Default floor',
      isSortable: true,
      order: 'none',
      attributeSort: 'floor',
    },
    {
      name: 'Sub settings',
      isSortable: false,
    },
    {
      name: 'Used on',
      isSortable: true,
      order: 'ascending',
      attributeSort: 'stacksUse',
    },
    {
      name: 'Actions',
      isSortable: false,
    },
  ])

  const [search, setSearch] = useState('')

  const prettyPrintFloorPrice = (floor: number) => displayWithCurrency((floor / 100).toFixed(2), currencySymbol)

  const flyout = useGlobalFlyout()
  const modal = useGlobalModal()
  const searchAttributes: (keyof FloorRuleListed)[] = ['name', 'floor']
  // Keep the table empty on error, otherwise the synthetic 'NO_RULE' row would hide the error state
  const floorPriceRulesWithNoRule: FloorRuleListed[] = isError
    ? []
    : [...floorPriceRules, { id: 'NO_RULE', floor: 0, name: 'No floor price', updatedBy: 'Pubstack', updatedAt: new Date(), scopeId: '', stacksUse: {} }]
  const searchedFloorPriceRules = floorPriceRulesWithNoRule.filter((f) => {
    return searchAttributes.some((attr) => {
      const value = f[attr]
      if (value) {
        if (attr === 'floor') {
          const floorPrice = prettyPrintFloorPrice(f.floor)
          return floorPrice.includes(search.toLowerCase()) || floorPrice.replace('.', ',').includes(search.toLowerCase())
        } else {
          return value.toString().toLowerCase().includes(search.toLowerCase())
        }
      }
    })
  })
  const sortedColumn = (columns.find((c) => c.isSortable && c.order !== 'none') || columns[0]) as SortableColumn<FloorRuleListed>
  let sortedFloorPriceRules: FloorRuleListed[] = []
  if (sortedColumn.attributeSort === 'stacksUse') {
    sortedFloorPriceRules = searchedFloorPriceRules.sort((a, b) => (sortedColumn.order === 'ascending' ? 1 : -1) * (countStacksUse(b.stacksUse) - countStacksUse(a.stacksUse)))
  } else {
    sortedFloorPriceRules = handleTableSort(columns, searchedFloorPriceRules)
  }

  const { pageItems, paginationProps, resetPage } = useClientPagination(sortedFloorPriceRules)

  const canEditFloorRule = (floorRule: FloorRuleListed) => canEdit && floorRule.id !== 'NO_RULE'

  /**
   * Why this row cannot be archived, or `undefined` when it can.
   *
   * The same refusal the API makes, said before the click rather than after it. "No floor price" is the
   * synthetic row the list adds rather than a stored rule, so there is nothing behind it to archive.
   */
  const archiveDisabledReason = (floorRule: FloorRuleListed): string | undefined => {
    if (floorRule.id === 'NO_RULE') {
      return "Can't archive a Pubstack rule"
    }
    const stackCount = countStacksUse(floorRule.stacksUse)
    if (stackCount > 0) {
      return `Used on ${stackCount} stack${stackCount === 1 ? '' : 's'}. Remove it from them first.`
    }
    return canArchive ? undefined : permissionDisabledReason
  }

  /**
   * What a row opens, and what the eye in its actions opens: reading the rule, not the form that changes
   * it. The row used to go straight to the editor, which made a glance at a rule's targets an edit session
   * to back out of — and left Pubstack's own rule unclickable, since it cannot be edited at all, when it is
   * exactly the one a publisher has no other way to read. Every rule can be read; the flyout's own footer
   * is where the ones that can be changed offer it.
   */
  const openFloorRuleFlyout = (floorRule: FloorRuleListed) =>
    flyout.open(
      FloorRuleFlyoutPage,
      // The width the other rule flyouts read at: a list of settings does not need 816px, and the three
      // rules should not open at three different sizes.
      { color: Colors.Forest, width: DETAILS_FLYOUT_WIDTH, closeTrackingLabel: 'adm-floor-price-rules-icon-view-close' },
      {
        floorRule,
        currencySymbol,
        onEditFloorRule: () => {
          flyout.close()
          onEditRule(floorRule.id)
        },
        canEdit: canEditFloorRule(floorRule),
      }
    )

  return (
    /* No `headerActions`: the title bar is the title and nothing else. Creating a rule acts on *this
       list*, so it moved down beside the rest of what is done to it — the order the medias and ad units
       lists already read in. */
    <PageContent className={className} breadcrumbs={breadcrumbs}>
      <RuleListToolbar
        search={search}
        onSearchChange={(next) => {
          setSearch(next)
          resetPage()
        }}
        onOpenHistory={
          floorPriceRulesHistory
            ? () => flyout.open(HistoryFlyout, { color: Colors.King, width: '816px', closeTrackingLabel: 'adm-floor-price-history-close' }, { item: 'Floor price rules', data: floorPriceRulesHistory })
            : undefined
        }
        historyTrackingLabel={'adm-floor-price-history-view'}
        createLabel={'New rule'}
        onCreate={onCreateRule}
        createTrackingLabel={'adm-floor-price-rules-new'}
        canCreate={canCreate}
        createDisabledReason={permissionDisabledReason}
        canSeeArchived={canArchive}
        archivedLabel={'Archived rules'}
        onNavigateToArchived={onNavigateToArchived}
        archivedTrackingLabel={'adm-floor-price-rules-archived-navigate'}
        actionsTrackingLabel={'adm-floor-price-rules-actions-view'}
      />
      <Table columns={columns} isLoading={isLoading} onClickHeading={(column) => onColumnSort(columns, column, setColumns)}>
        {isError && (
          <TableRow>
            <TableCell colspan={columns.length}>
              <EmptyTable>
                <div>
                  <h2>An error occurred when trying to display your floor price rules</h2>
                  <p>
                    Please refresh the page or contact <Link label={SUPPORT_MAIL} href={`mailto:${SUPPORT_MAIL}`} trackingLabel={'common-mail-to-support-navigate'} /> if the issue persists.
                  </p>
                </div>
              </EmptyTable>
            </TableCell>
          </TableRow>
        )}
        {pageItems.map((floorRule) => (
          <TableRow key={floorRule.id} clickable={{ onClick: () => openFloorRuleFlyout(floorRule), trackingLabel: 'adm-floor-price-rules-view' }}>
            <TableCell>{floorRule.name}</TableCell>
            <TableCell>{floorRule.id !== 'NO_RULE' ? prettyPrintFloorPrice(floorRule.floor) : '-'}</TableCell>
            <TableCell>{floorRule.settings ? floorRule.settings.length : '0'}</TableCell>
            <TableCell>{floorRule.id === 'NO_RULE' ? '-' : floorRule.stacksUse ? <PureUsedStackTooltip stacksBySite={floorRule.stacksUse} /> : <span>0 stack</span>}</TableCell>
            <ActionsTableCell>
              <ButtonActions>
                <Tooltip title={'View'}>
                  <Button
                    variant={'tertiary'}
                    iconName={'view'}
                    onClick={(event) => {
                      event.stopPropagation()
                      openFloorRuleFlyout(floorRule)
                    }}
                    trackingLabel={'adm-floor-rule-icon-view'}
                  />
                </Tooltip>

                <Tooltip
                  title={floorRule.id === 'NO_RULE' ? `Can't edit a Pubstack rule` : permissionDisabledReason && !canEdit ? permissionDisabledReason : 'Edit'}
                  positions={['left', 'bottom', 'top', 'right']}
                >
                  <Button
                    iconName={'edit'}
                    variant={'tertiary'}
                    title={'Edit'}
                    onClick={() => onEditRule(floorRule.id)}
                    disabled={!canEditFloorRule(floorRule)}
                    trackingLabel={'adm-floor-price-rules-icon-edit'}
                  />
                </Tooltip>

                {/* Owners only, and only for a rule nothing uses — the button stays on the row saying
                    which of the two it is, rather than disappearing and leaving the reader to guess
                    whether archiving exists at all. */}
                {canArchive && (
                  <Tooltip title={archiveDisabledReason(floorRule) ?? 'Archive'} positions={['left', 'bottom', 'top', 'right']}>
                    <Button
                      iconName={'archive'}
                      variant={'tertiary'}
                      title={'Archive'}
                      disabled={!!archiveDisabledReason(floorRule) || isArchiving}
                      onClick={(event) => {
                        event.stopPropagation()
                        modal.open(ArchiveRuleModal, {
                          name: floorRule.name,
                          noun: 'floor price rule',
                          onArchive: () => onArchiveRule(floorRule.id),
                          isPending: isArchiving,
                          trackingPrefix: 'adm-floor-price-rules-archive',
                        })
                      }}
                      trackingLabel={'adm-floor-price-rules-archive'}
                    />
                  </Tooltip>
                )}
              </ButtonActions>
            </ActionsTableCell>
          </TableRow>
        ))}
      </Table>
      <Pagination {...paginationProps} className={'mt-4'} />
    </PageContent>
  )
}

export const PureFloorPricePage = styled(UnstyledPureFloorPricePage)``
